/**
 * API 클라이언트 - 백엔드 API와 통신하는 유틸리티
 */

// 🔥 CRITICAL FIX: 정적 import로 변경하여 TDZ 오류 해결
import { auth } from '../firebase/firebaseConfig.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * HTTP 요청 헬퍼 메서드
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    // Firebase Auth 토큰을 가져와서 헤더에 추가
    // 🔥 CRITICAL FIX: 정적 import 사용 (이미 상단에서 import됨)
    if (auth && auth.currentUser) {
      try {
        const token = await auth.currentUser.getIdToken();
        defaultOptions.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.warn('Failed to get Firebase token:', error);
        // Fallback to localStorage token
        const fallbackToken = localStorage.getItem('adminToken');
        if (fallbackToken) {
          defaultOptions.headers.Authorization = `Bearer ${fallbackToken}`;
        }
      }
    } else {
      // Fallback to localStorage token
      const fallbackToken = localStorage.getItem('adminToken');
      if (fallbackToken) {
        defaultOptions.headers.Authorization = `Bearer ${fallbackToken}`;
      }
    }

    const config = {
      ...defaultOptions,
      ...options,
      mode: 'cors', // 🔥 CORS 모드 명시
      credentials: 'include' // 🔥 인증 정보 포함
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // 에러 응답 본문 추출 시도
        let errorData = {};
        let errorText = '';
        try {
          errorText = await response.text();
          if (errorText) {
            try {
              errorData = JSON.parse(errorText);
            } catch (jsonError) {
              // JSON이 아닌 경우 텍스트로 저장
              errorData = { raw: errorText };
            }
          }
        } catch (parseError) {
          console.warn('Failed to parse error response:', parseError);
          errorData = { parseError: parseError.message };
        }
        
        // 콘솔에 상세 에러 정보 출력
        console.error('❌ API Error Response:', {
          url,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          errorData,
          errorText: errorText.substring(0, 500) // 처음 500자만
        });
        
        // 에러 객체 생성 (response 정보 포함)
        const error = new Error(
          errorData.message || 
          errorData.error || 
          errorData.details || 
          errorData.raw ||
          `HTTP error! status: ${response.status}`
        );
        error.status = response.status;
        error.statusText = response.statusText;
        error.response = {
          data: errorData,
          status: response.status,
          statusText: response.statusText,
          rawText: errorText
        };
        error.name = response.status >= 500 ? 'ServerError' : 'ClientError';
        throw error;
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      // 이미 처리된 에러는 그대로 전달
      if (error.status && error.response) {
        throw error;
      }
      
      // 연결 오류 처리 (ERR_CONNECTION_REFUSED, ERR_CONNECTION_CLOSED 등)
      if (error.message?.includes('Failed to fetch') || 
          error.message?.includes('ERR_CONNECTION_REFUSED') ||
          error.message?.includes('ERR_CONNECTION_CLOSED') ||
          error.name === 'TypeError') {
        const friendlyError = new Error(
          `백엔드 서버에 연결할 수 없습니다. (${this.baseURL})\n` +
          `서버가 실행 중인지 확인하거나 환경 변수 VITE_API_BASE_URL을 설정해주세요.`
        );
        friendlyError.name = 'ConnectionError';
        throw friendlyError;
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * GET 요청
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET',
    });
  }

  /**
   * POST 요청
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT 요청
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE 요청
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * 파일 업로드 요청
   */
  async uploadFile(endpoint, file, additionalData = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    const token = localStorage.getItem('adminToken');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return this.request(endpoint, {
      method: 'POST',
      headers,
      body: formData,
    });
  }

  /**
   * 파일 다운로드 요청
   */
  async downloadFile(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const token = localStorage.getItem('adminToken');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }
}

// 싱글톤 인스턴스 생성
const apiClient = new ApiClient();

export default apiClient;
